import React, { useEffect, useState } from 'react';
import './blogPostPage.scss';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PagesHeroSection from '../../components/pagesHeroSection';
import { Container, Spinner } from 'react-bootstrap';
import emptyBlogImg from '../../assets/blog/no-blog-img.png';
import { CANONICAL_ORIGIN } from '../../utils/hoc/SEOUpdater';
import PageNotFound from '../404PageNotFound';
import {
  getCachedPost,
  loadBlogPost,
  pickLang,
  blogPostPath,
  formatPostDate,
  toParagraphs,
} from '../../utils/blogData';

/**
 * Post data comes from /blog-data/posts/<slug>.json (written at build time by
 * scripts/fetch-blog-data.js) and is preloaded before the first render on a
 * direct load (see src/utils/blogData.js). react-snap therefore prerenders
 * the real article, including the <head> tags set below, and the visitor's
 * first render is identical to that HTML - no spinner, no Firestore, no
 * hydration mismatch. This replaces the old Firestore fetch and the
 * hand-built static pages from scripts/inject-blog-posts.js.
 */

const PRACTICE_PATHS = {
  immigration: 'immigration',
  personalinjury: 'personalinjury',
  lemonlaw: 'lemonlaw',
  employmentlaw: 'employmentlaw',
};

// Sets title/meta/canonical/hreflang for this post. Runs during react-snap's
// prerender too, so these tags end up in the static HTML.
const usePostHead = (post, lang, slug) => {
  useEffect(() => {
    // undefined = still loading; null = no such post (PageNotFound sets its own noindex tags)
    if (post === undefined || post === null) return;

    const setMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    const removeHreflang = () =>
      document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    const setCanonical = (href) => {
      let el = document.head.querySelector('link[rel="canonical"]');
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    document.documentElement.lang = lang;

    const hasLang = post.langs.includes(lang);
    const title = pickLang(post.title, lang);
    const description = pickLang(post.summary, lang);
    const url = `${CANONICAL_ORIGIN}${blogPostPath(lang, slug)}`;
    const enUrl = `${CANONICAL_ORIGIN}${blogPostPath('en', slug)}`;
    const withBrand = `${title} | Avagyan Law Firm`;

    document.title = withBrand.length <= 62 ? withBrand : title;
    setMeta('name', 'description', description);
    // A language this post isn't translated into shows the English text:
    // keep it out of the index and point Google at the English original.
    setMeta('name', 'robots', hasLang ? 'index, follow' : 'noindex, follow');
    setCanonical(hasLang ? url : enUrl);
    setMeta('property', 'og:type', 'article');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', hasLang ? url : enUrl);
    setMeta('property', 'og:image', post.imgUrl || `${CANONICAL_ORIGIN}/og-preview.png`);
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    if (post.date) setMeta('property', 'article:published_time', post.date);

    removeHreflang();
    post.langs.forEach((l) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', l);
      link.setAttribute('href', `${CANONICAL_ORIGIN}${blogPostPath(l, slug)}`);
      document.head.appendChild(link);
    });
    const xDefault = document.createElement('link');
    xDefault.setAttribute('rel', 'alternate');
    xDefault.setAttribute('hreflang', 'x-default');
    xDefault.setAttribute('href', enUrl);
    document.head.appendChild(xDefault);
  }, [post, lang, slug]);
};

const BlogPostPage = () => {
  const { blogTitle: slug, lang } = useParams();
  const { t } = useTranslation('blogPage');

  const [post, setPost] = useState(() => getCachedPost(slug));
  const [error, setError] = useState(false);

  useEffect(() => {
    const cached = getCachedPost(slug);
    if (cached !== undefined) {
      setPost(cached);
      return undefined;
    }
    let cancelled = false;
    setPost(undefined);
    loadBlogPost(slug)
      .then((p) => {
        if (!cancelled) setPost(p);
      })
      .catch((err) => {
        console.error('Error loading blog post:', err);
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  usePostHead(post, lang, slug);

  // Unknown slug: show the site's normal 404 page. nginx serves 404.html (the
  // prerendered PageNotFound) for these URLs, so rendering the same component
  // here keeps hydration consistent.
  if (post === null) return <PageNotFound />;

  const loading = post === undefined && !error;
  const notFound = error;

  const title = post ? pickLang(post.title, lang) : '';
  const body = post ? pickLang(post.body, lang) : '';
  const heroImage = (post && post.imgUrl) || emptyBlogImg;
  const practice = post && post.category && PRACTICE_PATHS[post.category];

  const schema = post
    ? {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: title,
        description: pickLang(post.summary, lang),
        image: post.imgUrl || `${CANONICAL_ORIGIN}/og-preview.png`,
        inLanguage: post.langs.includes(lang) ? lang : 'en',
        ...(post.date && { datePublished: post.date }),
        ...(post.dateModified && { dateModified: post.dateModified }),
        author: { '@type': 'Organization', name: 'Avagyan Law Firm', url: `${CANONICAL_ORIGIN}/` },
        publisher: {
          '@type': 'Organization',
          name: 'Avagyan Law Firm',
          logo: { '@type': 'ImageObject', url: `${CANONICAL_ORIGIN}/logo512.png` },
        },
        mainEntityOfPage: `${CANONICAL_ORIGIN}${blogPostPath(lang, slug)}`,
      }
    : null;

  return (
    <article className="blog-post-page">
      {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}

      <PagesHeroSection page={loading ? '' : notFound ? t('notFoundTitle') : title} background={heroImage} />

      <Container className="py-5">
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <p className="mt-3">{t('loadingArticle')}</p>
          </div>
        )}

        {!loading && notFound && (
          <div className="text-center py-5">
            <p>{t('notFoundDescription')}</p>
            <Link to={`/${lang}/blog/`} className="blog-cta-link">
              {t('backToBlog')}
            </Link>
          </div>
        )}

        {post && (
          <>
            <header className="blog-header">
              {/* h2 styled as h1: the hero above renders the page's single <h1>. */}
              <h2 className="h1">{title}</h2>
              {post.date && (
                <p className="blog-date">
                  <time dateTime={post.date}>{formatPostDate(post.date)}</time>
                </p>
              )}
            </header>

            <section className="blog-body">
              {toParagraphs(body).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </section>

            {practice && (
              <aside className="blog-cta">
                <p className="blog-cta-text">{t(`cta_${post.category}`)}</p>
                <div className="blog-cta-links">
                  <Link to={`/${lang}/${practice}/`} className="blog-cta-link">
                    {t(`ctaLink_${post.category}`)}
                  </Link>
                  <Link to={`/${lang}/contact-us/`} className="blog-cta-link blog-cta-link--secondary">
                    {t('ctaContact')}
                  </Link>
                </div>
              </aside>
            )}

            <nav className="blog-back">
              <Link to={`/${lang}/blog/`}>{t('backToBlog')}</Link>
            </nav>
          </>
        )}
      </Container>
    </article>
  );
};

export default BlogPostPage;
